<template>
  <transition name="modal-fade">
    <div v-if="true" class="modal-overlay" @click="$emit('close')">
      <div class="modal-container" @click.stop>
        <!-- Modal Header -->
        <div class="modal-header">
          <div class="header-content">
            <div class="header-left">
              <font-awesome-icon icon="fa-solid fa-utensils" class="header-icon"/>
              <div>
                <h5>Cooking Instructions</h5>
                <p class="order-number-text">PLU: {{ plu }}</p>
              </div>
            </div>
            <button class="btn-close" @click="$emit('close')">
              <font-awesome-icon icon="fa-solid fa-xmark"/>
            </button>
          </div>
          <div class="gradient-accent"></div>
        </div>

        <!-- Modal Body -->
        <div class="modal-body">
          <div v-if="loading" class="loading-state">
            <div class="spinner"></div>
            <p>Loading instructions...</p>
          </div>

          <div v-else-if="instruction">
            <div v-if="currentGroup" class="instruction-group">
              <div class="group-header">
                <font-awesome-icon icon="fa-solid fa-list-check" class="group-icon"/>
                <h6 class="group-title">{{ currentGroup.name }}</h6>
              </div>
              <div class="instructions-list">
                <div class="instruction-item" v-for="message in sortedInstructions(currentGroup)" :key="message.CookingNum">
                  <label class="instruction-label" :class="{'selected': message.selected, 'wait': message.Wait}">
                    <input
                      v-if="shouldWait"
                      class="d-none"
                      type="checkbox"
                      v-model="message.selected"
                      @change="select(message)"
                    >
                    <div class="instruction-content" @click="!shouldWait ? gotoNext(message) : null">
                      <Instruction :group="currentGroup.name" :message="message" class="instruction-component"/>
                    </div>
                    <font-awesome-icon v-if="!shouldWait" icon="fa-solid fa-chevron-right" class="arrow-icon"/>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div v-else class="empty-state">
            <font-awesome-icon icon="fa-solid fa-circle-info" class="empty-icon"/>
            <p>No instructions found.</p>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="modal-footer">
          <button v-if="hasNext" class="btn-secondary" @click="previousGroup">
            <font-awesome-icon icon="fa-solid fa-arrow-left"/>
            Back
          </button>
          <div v-else></div>

          <button class="btn-primary" @click="nextGroup">
            {{ text }}
            <font-awesome-icon v-if="text === 'Next'" icon="fa-solid fa-arrow-right"/>
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import Instruction from "@/components/point-of-sale/sales/cooking-instructions/Instruction";
import {Storage} from "@/services/Storage";
import { responses } from "@/utils/responses";

export default {
  name: "CookingInstructionModal",
  components: { Instruction },
  props: {
    plu: {
      type: [String, Number],
      required: true
    },
    saleNumber: {
      type: [String, Number],
      default: ''
    },
    cookingLink: {
        type: [String, Number],
        default: null
    }
  },
  data() {
    return {
      index: 0,
      loading: false,
      instruction: undefined,
      selectedInstructions: [],
      instructionIds: new Set(),
    }
  },
  computed: {
    hasNext() {
      return this.index > 0;
    },
    currentGroup() {
      if (!this.instruction || !this.instruction.groups) return null;
      return this.instruction.groups[this.index];
    },
    shouldWait() {
      if (!this.currentGroup) return false;
      let messages = this.sortedInstructions(this.currentGroup);
      let waitingMessages = messages.filter(message => message.Wait);
      return (this.currentGroup.confirm) || waitingMessages.length >= 1;
    },
    groupSize() {
      return (this.instruction && this.instruction.groups) ? this.instruction.groups.length : 0
    },
    text() {
      let lastIndex = this.groupSize - 1
      if (this.index === lastIndex) {
        return 'Confirm'
      }
      return 'Next'
    }
  },
  mounted() {
    this.loadInstructions();
  },
  methods: {
    loadInstructions() {
        this.loading = true;
        
        if (this.cookingLink == 10000) {
           // Custom instructions logic
           this.instruction = {
                groups: [{
                  name: "Custom Instructions",
                  confirm: true,
                  messages: []
                }]
           };
           this.loading = false;
           // Prompt for custom instruction immediately
           this.promptCustomInstruction();
        } else {
            this.$store.dispatch('getCookingInstruction', this.plu)
              .then(res => {
                  this.instruction = res;
              })
              .catch(err => {
                  console.error("Failed to load instructions", err);
                  responses.showError("Failed to load instructions");
                  this.$emit('close');
              })
              .finally(() => {
                  this.loading = false
              })
        }
    },
    async promptCustomInstruction() {
         const { value: text } = await Swal.fire({
          title: 'Enter Instruction',
          input: 'text',
          inputLabel: 'Custom Instruction',
          inputPlaceholder: 'Enter instruction...',
          showCancelButton: true
        });

        if (text) {
             const instructionId = Date.now();
              const customInstruction = {
                CookingMsg: text,
                CookingNum: instructionId,
                Price: 0,
                Printer: '',
                Qty: 1,
                StockCode: '0',
                Wait: true,
                count: 1,
                selected: true
              };
              
              this.currentGroup.messages.push(customInstruction);
               if (!this.instructionIds.has(instructionId)) {
                this.selectedInstructions.push(customInstruction);
                this.instructionIds.add(instructionId);
              }
        } else if (this.selectedInstructions.length === 0) {
            // If cancelled and no instructions, close modal
            this.$emit('close');
        }
    },
    select(v) {
      if (v.selected) {
        if (!this.instructionIds.has(v.CookingNum)) {
          this.selectedInstructions.push(v);
          this.instructionIds.add(v.CookingNum);
        }
      } else {
        this.selectedInstructions = this.selectedInstructions.filter(
          instruction => instruction.CookingNum !== v.CookingNum
        );
        this.instructionIds.delete(v.CookingNum);
      }
    },
    gotoNext(message) {
      if (!message.selected) {
        message.selected = true;
        this.select(message);
        // Also mark in the group messages for UI state
        if (this.currentGroup) {
             this.currentGroup.messages = this.currentGroup.messages.map(msg => {
              if (message.CookingNum === msg.CookingNum) {
                msg.selected = true;
                return msg;
              }
              return msg;
            });
        }
      }
      this.nextGroup();
    },
    async nextGroup() {
      if (this.text === 'Confirm') {
        let selected = [];
        if (this.currentGroup) {
            selected = this.currentGroup.messages.filter(m => m.selected === true);
        }
        // Fallback to our accumulated selectedInstructions if the group filtering misses something
        // (though logic suggests we should focus on the cumulative set)
        
        await this.addInstructionToItem();
        return;
      }
      
      let lastIndex = this.groupSize - 1
      if (this.index < lastIndex) {
        this.index++
      }
    },
    previousGroup() {
      if (this.index > 0) {
        this.index--
      }
    },
    async addInstructionToItem() {
      const item = this.$store.getters.getByPlu(this.plu);
      if (!item) {
          responses.showError("Item not found");
          this.$emit('close');
          return;
      }

      let price = item.SellingIncl;
      
      let specialPrice = this.$store.getters.getItemSpecialPrice(item.PLU);
      if (specialPrice) {
        responses.showSuccess(`Special Detected`);
        price = specialPrice;
      }

      let tourismLevy = 0.0;
      let set = await Storage.getStockSetting(item.PLU);
      if (set && set.tourism){
        let obj = { amount : price, incl : true }
        tourismLevy = await this.$store.dispatch('getTourismLevyAmount', obj);
      }

      const uniqueInstructions = [];
      const seenIds = new Set();
      
      for (const instruction of this.selectedInstructions) {
        if (!seenIds.has(instruction.CookingNum)) {
          uniqueInstructions.push(instruction);
          seenIds.add(instruction.CookingNum);
        }
      }
      
      let obj = {
        Plu: item.PLU,
        Description: item.Description,
        Qty: 1,
        Unit: price,
        tourismLevy,
        Line: price,
        Discount: 0,
        vat: 0,
        count: 1,
        instructions: uniqueInstructions
      };

      this.$store.commit('addToCurrentSaleLines', obj);
      this.$store.dispatch(`calculateTipAmount`);
      responses.showSuccess('Instructions Added');
      
      this.$emit('close');
    },
    sortedInstructions(group) {
      if (!group || !group.messages) return [];
      return group.messages.filter(m1 => !m1.CookingMsg.includes("***DONE***"))
          .map(i => {
           // Don't reset selected=false here, it wipes state on re-render!
           // If 'selected' isn't on the object, initialize it.
            if (i.selected === undefined) i.selected = false;
            return i;
          }).sort((m1, m2) => {
            let i = m1.CookingMsg.localeCompare(m2.CookingMsg)
            if (m1.Wait) {
              return 1;
            }
            return i;
          })
    }
  }
}
</script>

/* Modern Modal Styles */
<style lang="scss" scoped>
/* Modal Overlay */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  overflow-y: auto;
}

/* Modal Container */
.modal-container {
  background: var(--card-bg);
  border-radius: 20px;
  max-width: 700px;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: modalSlideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  @media (max-width: 640px) {
    max-width: 100%;
    max-height: 100vh;
    border-radius: 0;
  }
}

/* Modal Header */
.modal-header {
  position: relative;
  flex-shrink: 0;
  padding: 24px;
  background: linear-gradient(135deg, var(--accent-electric-blue) 0%, var(--accent-neon-purple) 100%);
  color: white;
  border-radius: 20px 20px 0 0;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image:
      radial-gradient(circle at 20% 30%, rgba(255, 255, 255, 0.15) 0%, transparent 25%),
      radial-gradient(circle at 80% 70%, rgba(255, 255, 255, 0.1) 0%, transparent 25%);
    opacity: 0.6;
  }

  .gradient-accent {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: rgba(255, 255, 255, 0.3);
  }
}

.header-content {
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;

  .header-icon {
    font-size: 2rem;
  }

  h5 {
    margin: 0 0 4px 0;
    font-size: 1.5rem;
    font-weight: 700;
  }

  .order-number-text {
    margin: 0;
    font-size: 0.95rem;
    opacity: 0.9;
  }
}

.btn-close {
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 12px;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: rotate(90deg);
  }
}

/* Modal Body */
.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 24px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(var(--accent-electric-blue-rgb), 0.3);
    border-radius: 3px;
  }
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 16px;

  .spinner {
    width: 48px;
    height: 48px;
    border: 4px solid rgba(var(--accent-electric-blue-rgb), 0.2);
    border-top-color: var(--accent-electric-blue);
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  p {
    color: var(--text-secondary);
    font-size: 0.95rem;
    margin: 0;
  }
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  animation: fadeIn 0.5s ease;

  .empty-icon {
    font-size: 3rem;
    color: var(--text-secondary);
    opacity: 0.3;
    margin-bottom: 16px;
  }

  p {
    color: var(--text-secondary);
    font-size: 0.95rem;
    margin: 0;
  }
}

/* Instruction Group */
.instruction-group {
  background: var(--secondary-button-bg);
  border-radius: 14px;
  padding: 20px;
  border: 2px solid transparent;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: rgba(var(--accent-electric-blue-rgb), 0.2);
  }
}

.group-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;

  .group-icon {
    color: var(--accent-electric-blue);
    font-size: 1.3rem;
  }

  .group-title {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--text-primary);
  }
}

/* Instructions List */
.instructions-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.instruction-item {
  .instruction-label {
    display: flex;
    align-items: center;
    gap: 12px;
    cursor: pointer;
    background: var(--card-bg);
    border-radius: 12px;
    padding: 16px;
    border: 2px solid rgba(var(--accent-electric-blue-rgb), 0.1);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
      border-color: rgba(var(--accent-electric-blue-rgb), 0.3);
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(var(--accent-electric-blue-rgb), 0.15);
    }

    &.selected {
      border-color: var(--accent-electric-blue);
      background: rgba(var(--accent-electric-blue-rgb), 0.08);
    }

    &.wait {
      background: rgba(var(--warning-rgb), 0.08);
      border-color: rgba(var(--warning-rgb), 0.3);
    }
  }

  .instruction-content {
    flex: 1;
    display: flex;
    align-items: center;
  }

  .instruction-component {
    width: 100%;
  }

  .arrow-icon {
    color: var(--text-secondary);
    font-size: 1.2rem;
    transition: all 0.2s ease;
  }

  &:hover .arrow-icon {
    color: var(--accent-electric-blue);
    transform: translateX(4px);
  }
}

/* Modal Footer */
.modal-footer {
  flex-shrink: 0;
  padding: 20px 24px;
  border-top: 1px solid rgba(var(--accent-electric-blue-rgb), 0.1);
  display: flex;
  gap: 12px;
  justify-content: space-between;

  @media (max-width: 640px) {
    flex-direction: column;
  }
}

.btn-secondary, .btn-primary {
  padding: 12px 24px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  border: none;
}

.btn-secondary {
  background: var(--secondary-button-bg);
  color: var(--text-primary);

  &:hover {
    background: var(--secondary-button-bg-dark);
  }
}

.btn-primary {
  background: linear-gradient(135deg, var(--accent-electric-blue) 0%, var(--accent-neon-purple) 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(var(--accent-electric-blue-rgb), 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(var(--accent-electric-blue-rgb), 0.4);
  }
}

/* Animations */
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes modalSlideUp {
  from {
    opacity: 0;
    transform: translateY(40px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-fade-enter-active, .modal-fade-leave-active {
  transition: opacity 0.3s ease;

  .modal-container {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
}

.modal-fade-enter-from, .modal-fade-leave-to {
  opacity: 0;

  .modal-container {
    transform: translateY(40px) scale(0.95);
  }
}
</style>
